import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Smartphone, Copy, Check, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { patientLinkService } from '../services/api'
import { useOnboardingStore } from '../store/onboardingStore'
import { logger } from '../utils/logger'

// Keys that make up a resumable draft — the persisted store minus the
// session token (which is device-bound and reissued on exchange).
const DRAFT_KEYS = [
  'currentStep', 'phoneNumber', 'personalData', 'addressData', 'addressCheck', 'insuranceData',
  'guardianData', 'uploadedDocuments', 'stepUploads', 'yousignRequestId', 'yousignSignerId', 'yousignDocuments',
  'yousignDataHash', 'yousignPhoneNumber', 'cardSaved', 'cardOptOut', 'sepaDropinCompleted',
] as const

export function buildDraft(): Record<string, unknown> {
  const state = useOnboardingStore.getState() as unknown as Record<string, unknown>
  return Object.fromEntries(DRAFT_KEYS.map(k => [k, state[k]]))
}

export function HandoffModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const patientNumber = useOnboardingStore(s => s.patientNumber)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    patientLinkService.handoff(patientNumber, buildDraft())
      .then(r => setUrl(r.url))
      .catch(err => { logger.error('[Handoff] mint failed:', err); setError(true) })
  }, [patientNumber])

  const copy = async () => {
    if (!url) return
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { toast.error(t('handoff.copyFailed')) }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-h3 text-foreground flex items-center gap-2"><Smartphone className="w-5 h-5" />{t('handoff.title')}</h2>
          <button type="button" onClick={onClose} aria-label="close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-small text-muted-foreground mb-5">{t('handoff.subtitle')}</p>
        {error ? (
          <p className="text-small text-destructive">{t('handoff.error')}</p>
        ) : !url ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <div className="flex justify-center mb-5"><QRCodeSVG value={url} size={192} includeMargin /></div>
            <button type="button" onClick={copy} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded border border-border text-small hover:bg-muted">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t('handoff.copied') : t('handoff.copyLink')}
            </button>
            <p className="text-xs text-muted-foreground mt-4">{t('handoff.expiry')}</p>
          </>
        )}
      </div>
    </div>
  )
}
